/**
 * FertilizationSpecimenPicker
 *
 * Lists frozen sperm vials the patient can point at when logging a
 * fertilization (IVF, ICSI) or a sperm-collection step whose sample was
 * frozen ahead of time. Single-select.
 *
 * Picking writes nothing to the vault. The choice is the step's
 * `semenSpecimenId`, which the phase save carries onto the spermiogram row;
 * the vault learns the vial was used when fertilization is saved (#3628),
 * and picking material for a cycle no longer puts it on hold (#3750).
 *
 * Visual language mirrors the CryoVault's `NestedSpecimenTile`: the sperm
 * `OrganicMiniIcon`, "Sample #N" numbering minted through the same
 * `buildCryoSpecimenLabel` helper (so #3 here matches #3 in her vault),
 * and the facility name as the primary subtitle. Iterating over the
 * *whole* specimen list — not just semen — is deliberate: the shared
 * per-type counter in `mapApiSpecimensToCryoDetails` is what keeps the
 * numbering identical across surfaces.
 *
 * Each row has an eye toggle. The lab figures (motility, concentration,
 * collected date) live behind it because they'd crowd the clinic name in
 * the default view; her identity for the vial is the clinic, not the
 * numbers.
 */

import { useMemo, useState } from "react";
import { View } from "react-native";
import { Check, Eye, EyeOff } from "lucide-react-native";
import {
  IconButton,
  ListRowSkeleton,
  MetaText,
  EmptyState,
  Row,
  SupportingSubtitleText,
} from "@louise/moon-ui";
import { semanticColors, withAlpha } from "@louise/theme/tokens";
import { useTranslations, type Locale } from "@src/packages/i18n";
import { useLocale } from "@src/packages/i18n/useLocale";
import { formatDate } from "@src/packages/i18n/formatters";
import {
  useSpecimens,
  type Specimen,
} from "@src/packages/care/cryovault/useSpecimens";
import { isInFrozenStorage } from "@src/packages/care/cryovault/frozenSpecimenAvailability";
import { OrganicMiniIcon } from "@src/packages/homepage/plan/components/OrganicMiniIcon";
import { buildCryoSpecimenLabel } from "../../../../components/CryoSpecimenDetailModal";

const NS = "care.treatment_hub.fertilization_specimen_picker";

type Props = {
  value: string | null;
  onChange: (specimenId: string | null) => void;
};

type LabeledSpecimen = { specimen: Specimen; label: string };

function activeFacility(specimen: Specimen): string | null {
  return (
    specimen.storageLocations.find(
      (location) => location.isActive && location.storageType === "frozen",
    )?.facility ?? null
  );
}

/** Compact details shown when the eye toggle is expanded on a row. */
function detailLines(
  t: ReturnType<typeof useTranslations>,
  locale: Locale,
  specimen: Specimen,
): string[] {
  const lines: string[] = [];
  const motility = specimen.spermiogram?.motility;
  if (typeof motility === "number") {
    lines.push(
      t("cryo_vault.motility_suffix", {
        params: { value: `${motility}%` },
      }),
    );
  }
  const concentration = specimen.spermiogram?.spermConcentration;
  if (typeof concentration === "number") {
    lines.push(
      t(`${NS}.concentration_value`, {
        params: { value: String(concentration) },
      }),
    );
  }
  if (specimen.collectedAt) {
    const formatted = formatDate(specimen.collectedAt, locale, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    if (formatted) {
      lines.push(t(`${NS}.collected_on`, { params: { date: formatted } }));
    }
  }
  return lines;
}

export function FertilizationSpecimenPicker({ value, onChange }: Props) {
  const t = useTranslations();
  const { locale } = useLocale();
  const { data: allSpecimens, isLoading } = useSpecimens();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const semenWithLabels: LabeledSpecimen[] = useMemo(() => {
    if (!allSpecimens) return [];
    const counters: Record<string, number> = {};
    const result: LabeledSpecimen[] = [];
    for (const specimen of allSpecimens) {
      const localType = specimen.type === "semen" ? "sperm" : specimen.type;
      counters[localType] = (counters[localType] ?? 0) + 1;
      if (specimen.type !== "semen") continue;
      result.push({
        specimen,
        label: buildCryoSpecimenLabel(t, "semen", counters[localType]!),
      });
    }
    return result;
  }, [allSpecimens, t]);

  // The vial she already chose stays listed even once it has left the tank:
  // saving fertilization records it as used, and without this a re-opened
  // step would lose its answer to a required field.
  const selectable = useMemo(
    () =>
      semenWithLabels.filter(
        ({ specimen }) => specimen.id === value || isInFrozenStorage(specimen),
      ),
    [semenWithLabels, value],
  );

  if (isLoading) {
    return (
      <View testID="fertilization-specimen-picker-loading">
        {[0, 1].map((row) => (
          <ListRowSkeleton key={row} showLeading showSubtitle />
        ))}
      </View>
    );
  }

  if (selectable.length === 0) {
    return (
      <EmptyState
        illustration={<OrganicMiniIcon kind="sperm" size={24} />}
        title={t(`${NS}.empty`)}
        iconOrb={false}
        density="compact"
      />
    );
  }

  return (
    <View style={{ gap: 8 }}>
      {selectable.map(({ specimen, label }) => {
        const selected = specimen.id === value;
        const facility = activeFacility(specimen);
        const expanded = expandedId === specimen.id;
        const details = expanded ? detailLines(t, locale, specimen) : [];
        return (
          <Row
            key={specimen.id}
            surface="input"
            leading={<OrganicMiniIcon kind="sperm" size={28} />}
            title={label}
            titleNumberOfLines={1}
            subtitle={
              facility || details.length > 0 ? (
                <View style={{ gap: 2 }}>
                  {facility ? (
                    <SupportingSubtitleText numberOfLines={1}>
                      {facility}
                    </SupportingSubtitleText>
                  ) : null}
                  {details.map((line) => (
                    <MetaText key={line}>{line}</MetaText>
                  ))}
                </View>
              ) : undefined
            }
            trailing={
              <View
                style={{ flexDirection: "row", alignItems: "center", gap: 4 }}
              >
                {selected ? (
                  <Check
                    size={16}
                    color={semanticColors.accent.default}
                    strokeWidth={2}
                  />
                ) : null}
                <IconButton
                  accessibilityLabel={t(
                    expanded ? `${NS}.hide_details` : `${NS}.show_details`,
                  )}
                  action={
                    expanded
                      ? "hide_fertilization_specimen_details"
                      : "show_fertilization_specimen_details"
                  }
                  onPress={() => setExpandedId(expanded ? null : specimen.id)}
                  size={28}
                  hitSlop={6}
                >
                  {expanded ? (
                    <EyeOff
                      size={16}
                      color={semanticColors.text.tertiary}
                      strokeWidth={1.7}
                    />
                  ) : (
                    <Eye
                      size={16}
                      color={semanticColors.text.tertiary}
                      strokeWidth={1.7}
                    />
                  )}
                </IconButton>
              </View>
            }
            chevron={false}
            onPress={() => onChange(selected ? null : specimen.id)}
            action="pick_fertilization_specimen"
            accessibilityRole="radio"
            accessibilityLabel={label}
            accessibilityState={{ selected, checked: selected }}
            testID={`fertilization-specimen-${specimen.id}`}
            style={
              selected
                ? {
                    backgroundColor: semanticColors.accent.tint,
                    borderColor: withAlpha(semanticColors.accent.default, 0.13),
                  }
                : undefined
            }
          />
        );
      })}
    </View>
  );
}
